"use client";
 
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { type Locale, defaultLocale, getTranslation } from "./index";
 
type LanguageContextType = {
  locale: Locale;
  language: Locale; // 新增 language 属性作为 locale 的别名
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
  formatMessage: (key: string, values?: Record<string, string | number>) => string;
};
 
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
 
export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<Locale>(defaultLocale);
  const [isMounted, setIsMounted] = useState(false);
 
  useEffect(() => {
    setIsMounted(true);
  }, []);
 
  useEffect(() => {
    if (isMounted && typeof window !== 'undefined') {
      try {
        const savedLocale = localStorage.getItem("locale")  as Locale | null;
        if (savedLocale && ["en", "fr", "it", "zh"].includes(savedLocale)) {
          setLocale(savedLocale);
        }
      } catch (error) {
        console.warn("Could  not access localStorage:", error);
      }
    }
  }, [isMounted]);
 
  useEffect(() => {
    if (isMounted && typeof window !== 'undefined') {
      try {
        localStorage.setItem("locale",  locale);
      } catch (error) {
        console.warn("Could  not save to localStorage:", error);
      }
    }
  }, [locale, isMounted]);
 
  const t = (key: string): string => {
    const keys = key.split("."); 
    let value: unknown = getTranslation(locale);
 
    for (const k of keys) {
      if (value && typeof value === "object" && k in (value as Record<string, unknown>)) {
        value = (value as Record<string, unknown>)[k];
      } else {
        console.warn(`Translation  key not found: ${key}`);
        return key;
      }
    }
 
    return typeof value === "string" ? value : key;
  };
 
  const formatMessage = (key: string, values: Record<string, string | number> = {}): string => {
    let message = t(key);
 
    for (const [key, value] of Object.entries(values))  {
      if (key === "count" && typeof value === "number") {
        message = message.replace(new  RegExp(`\\{${key}, plural, (.+?)\\}`, "g"), (match, options) => {
          const parts = options.split(/\s+/); 
          let result = "";
 
          for (const part of parts) {
            const [condition, content] = part.split(/\s+(.+)/); 
 
            if (condition === "one" && value === 1) {
              result = content.replace(/[{}]/g,  "");
              break;
            }
 
            if (condition === "other" && value !== 1) {
              result = content.replace(/[{}]/g,  "");
              break;
            }
          }
 
          return result;
        });
      }
 
      message = message.replace(new  RegExp(`\\{${key}\\}`, "g"), String(value));
    }
 
    return message;
  };
 
  const value = {
    locale,
    language: locale, // 新增 language 作为 locale 的别名 
    setLocale,
    t,
    formatMessage,
  };
 
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
 
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
